package fr.frux.feature.mytrees

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import fr.frux.app.resources.Res
import fr.frux.app.resources.mytrees_empty_cta
import fr.frux.app.resources.mytrees_empty_message
import fr.frux.app.resources.mytrees_empty_title
import fr.frux.app.resources.mytrees_title
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.TreeCard
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.treeMarkerTint
import fr.frux.core.designsystem.theme.FruxSpacing
import org.jetbrains.compose.resources.stringResource

/**
 * « Mes arbres » — arbres créés par l'utilisateur, atteint depuis Profil.
 *
 * Sous-page : pas de padding de barre du bas, une flèche de retour dans la barre du haut.
 */
@Composable
fun MyTreesScreen(
    onAddTree: () -> Unit,
    onBack: () -> Unit,
    onTreeClick: (Long) -> Unit,
    viewModel: MyTreesViewModel = rememberMyTreesViewModel(),
) {
    val state by viewModel.state.collectAsState()

    // Même raisonnement que FavoritesScreen (constat #1 de la revue favoris) : suppression
    // d'un arbre depuis sa fiche, retour ici — le ViewModel survit au push/pop mais la
    // composition de cet écran est démontée puis remontée par Nav3, donc ce
    // LaunchedEffect(Unit) repart de zéro à chaque retour et recharge la liste.
    LaunchedEffect(viewModel) {
        viewModel.load()
    }

    Scaffold(
        topBar = { FruxTopBar(title = stringResource(Res.string.mytrees_title), onBack = onBack) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        when {
            state.isLoading -> {
                LoadingState(modifier = Modifier.padding(paddingValues))
            }

            state.error != null -> {
                ErrorState(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::load,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            state.trees.isEmpty() -> {
                EmptyState(
                    icon = Icons.Outlined.Park,
                    title = stringResource(Res.string.mytrees_empty_title),
                    message = stringResource(Res.string.mytrees_empty_message),
                    ctaLabel = stringResource(Res.string.mytrees_empty_cta),
                    onCta = onAddTree,
                    modifier = Modifier.padding(paddingValues),
                )
            }

            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(paddingValues),
                    contentPadding = PaddingValues(FruxSpacing.md),
                    verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    items(state.trees, key = { it.id }) { tree ->
                        TreeCard(
                            name = tree.name,
                            meta = tree.meta,
                            status = tree.status,
                            onClick = { onTreeClick(tree.id) },
                            glyph = tree.glyph,
                            glyphTint = treeMarkerTint(tree.scientificName, tree.status),
                            thumbUrl = tree.photoUrl,
                        )
                    }
                }
            }
        }
    }
}
